import { ScriptBlock, Act, Scene, Character } from '../types';

/**
 * Links script blocks to canonical structure IDs (acts, scenes) by matching
 * heading blocks to the classified structure data.
 *
 * The classified structure (from holistic AI extraction) provides canonical IDs
 * like `act-1`, `scene-1-1`. This function walks blocks sequentially, matching
 * heading text to act/scene names, then cascades those IDs to subsequent blocks.
 *
 * Also handles characterId linking: dialogue/lyric blocks inherit the character
 * from the preceding character_name block.
 */
export function linkBlocksToStructure(
    blocks: ScriptBlock[],
    acts: Act[],
    scenes: Scene[],
    characters: Character[] = []
): ScriptBlock[] {
    // Build lookup maps: normalized name → structure ID
    const actLookup = new Map<string, string>();
    for (const act of acts) {
        if (act.isPreset) continue; // Skip the Preset act
        actLookup.set(normalize(act.name), act.id);
    }

    const sceneLookup = new Map<string, string>();
    for (const scene of scenes) {
        sceneLookup.set(normalize(scene.name), scene.id);
    }
    const characterLookup = buildCharacterLookup(characters);

    // Build act → sceneIds map for fallback scene-to-act resolution
    const sceneToActMap = new Map<string, string>();
    for (const act of acts) {
        for (const sceneId of act.sceneIds) {
            sceneToActMap.set(sceneId, act.id);
        }
    }

    let currentActId: string | null = null;
    let currentSceneId: string | null = null;
    let lastCharacterId: string | null = null;

    const linkedBlocksByIndex = new Map<number, ScriptBlock>();
    const documentOrderedBlocks = blocks
        .map((block, originalIndex) => ({ block, originalIndex }))
        .toSorted((left, right) => {
            const pageComparison = left.block.pageNumber - right.block.pageNumber;
            if (pageComparison !== 0) return pageComparison;

            const indexComparison = left.block.blockIndex - right.block.blockIndex;
            if (indexComparison !== 0) return indexComparison;

            return left.originalIndex - right.originalIndex;
        });

    for (const { block, originalIndex } of documentOrderedBlocks) {
        // Match heading blocks to canonical structure
        if (block.type === 'heading') {
            const normalizedText = normalize(block.text);

            // Try exact match first, then fuzzy containment
            const matchedActId = findMatch(normalizedText, actLookup);
            const matchedSceneId = findMatch(normalizedText, sceneLookup);

            if (matchedActId) {
                currentActId = matchedActId;
                currentSceneId = null; // Act boundary resets scene
                lastCharacterId = null;
            } else if (matchedSceneId) {
                currentSceneId = matchedSceneId;
                lastCharacterId = null;
                currentActId = sceneToActMap.get(matchedSceneId) ?? currentActId;
            }
        }

        if (block.sceneId) {
            currentSceneId = block.sceneId;
            currentActId = sceneToActMap.get(block.sceneId) ?? block.actId ?? currentActId;
        }

        // Track character from character_name blocks
        if (block.type === 'character_name') {
            lastCharacterId = normalizeBlockCharacterId(block.characterId, characterLookup)
                ?? normalizeBlockCharacterId(block.text, characterLookup);
        }

        const resolvedSceneId = block.sceneId ?? currentSceneId;
        const resolvedActId = resolvedSceneId
            ? sceneToActMap.get(resolvedSceneId) ?? block.actId ?? currentActId
            : block.actId ?? currentActId;
        const characterId = normalizeBlockCharacterId(block.characterId, characterLookup);

        linkedBlocksByIndex.set(originalIndex, {
            ...block,
            actId: resolvedActId,
            sceneId: resolvedSceneId,
            characterId: (block.type === 'dialogue' || block.type === 'lyric')
                ? characterId ?? lastCharacterId
                : characterId,
        });
    }

    return blocks.map((_, index) => linkedBlocksByIndex.get(index)!);
}

export function normalizeCharacterId(value: string): string {
    const normalized = value
        .trim()
        .replace(/\s*\([^)]*\)\s*$/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

    return normalized || 'character';
}


// ============================================
// Internal Helpers
// ============================================

/** Normalize text for fuzzy matching: lowercase, collapse whitespace, strip punctuation */
function normalize(text: string): string {
    return text
        .toLowerCase()
        .replace(/[^\w\s]/g, '') // strip punctuation
        .replace(/\s+/g, ' ')   // collapse whitespace
        .trim();
}

/**
 * Find a match in the lookup map. Tries exact match first, then checks
 * if the text contains a lookup key (or vice versa).
 */
function findMatch(normalizedText: string, lookup: Map<string, string>): string | null {
    // Exact match
    if (lookup.has(normalizedText)) {
        return lookup.get(normalizedText)!;
    }

    // Containment match: check if text contains a key or key contains the text
    for (const [key, id] of lookup) {
        if (normalizedText.includes(key) || key.includes(normalizedText)) {
            return id;
        }
    }

    return null;
}

function buildCharacterLookup(characters: Character[]): Map<string, string> {
    const lookup = new Map<string, string>();
    for (const character of characters) {
        lookup.set(normalize(character.name), character.id);
        lookup.set(normalizeCharacterId(character.name), character.id);
        lookup.set(normalizeCharacterId(character.id), character.id);
    }
    return lookup;
}

function normalizeBlockCharacterId(value: string | null, characterLookup: Map<string, string>): string | null {
    if (!value) return null;

    const normalizedText = normalize(value);
    const normalizedId = normalizeCharacterId(value);
    return characterLookup.get(normalizedText) ?? characterLookup.get(normalizedId) ?? normalizedId;
}
